import { useEffect, useMemo, useState } from "react";
import CustomDatepicker from "../../../../../components/common/Forms/Datepicker";
import Table from "../../../../../components/common/Table/Table";
import Dropdown from "../../../../../components/common/AdminDropdown";
import Notifications from "../../../../../components/common/Notifications";
import { useDispatch } from "react-redux";
import { formatTimestampMDYHMS } from "../../../../../utils/date";
import { useAppSelector } from "../../../../../store/hooks";
import {
  getTuitionAdminComments,
  saveTuitionAdminComment,
  patchTuitionAdminEditComment,
  fetchTuitionOptions,
} from "../../../../../store/slices/Tuition/tuitionAdminSlice";
import { safeGet } from "../../../../../utils/safeGet";
import { trackButtonClick, trackFormSave } from '../../../../../services/analytics';

interface CommentsTabProps {
  applicationData?: {
    applicationId: string;
    employeeNuId?: string;
  };
}
const EditButton = ({
  onClick,
  disabled,
}: {
  onClick?: () => void;
  disabled?: boolean;
}) => (
  <button
    className={`button -primary -small inline-flex items-center whitespace-nowrap min-w-max!
      ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
    onClick={onClick}
    disabled={disabled}
  >
    <span className="whitespace-nowrap">Edit content</span>
  </button>
);

const Comments = ({ applicationData }: CommentsTabProps) => {
  const [showCommentBox, setShowCommentBox] = useState(false);
  const [newCommentDate, setNewCommentDate] = useState<string>("");
  const [newCommentType, setNewCommentType] = useState<string>("--Select--");
  const [newCommentText, setNewCommentText] = useState<string>("");
  // Inline edit state for an existing table row
  const [editingRowIndex, setEditingRowIndex] = useState<number | null>(null);
  const [editingText, setEditingText] = useState<string>("");
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);

 
  // Error banner state
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [errorHeader, setErrorHeader] = useState<string>("");
  // Field-level errors
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  // Success notification state
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  // Patch comment error state
  const [showPatchErrorNotification, setShowPatchErrorNotification] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
  const applicationId = applicationData?.applicationId;
  const nuid = applicationData?.employeeNuId;

  // Use shared util for timestamp formatting

  // Document table columns
  const documentColumns = [
    { key: "commentDt", label: "Date" },
    { key: "commentDateType", label: "Date/comment Type" },
    { key: "comments", label: "Content ", className: "!max-w-[400px] !break-words whitespace-normal" },
    { key: "lastUpdatedBy", label: "Last updated by" },
    { key: "lastUpdatedTime", label: "Last updated time" },
    { key: "action", label: "Edit" },
  ];

  const dispatch = useDispatch();
  const comments = useAppSelector(
    (state: any) => safeGet(state.tuitionAdmin?.comments, [])
  );
  const commentTypeOptions = useAppSelector(
    (state: any) => safeGet(state.tuitionAdmin?.commentTypeOptions, [])
  );
  const patchCommentError = useAppSelector(
    (state: any) => safeGet(state.tuitionAdmin?.patchCommentError, null)
  );

  // Logged-in user identifier from sessionStorage (NUID/userId)
  const loggedInUserId = useMemo(() => {
    try {
      return safeGet(sessionStorage.getItem("userId"), "").trim();
    } catch {
      return "";
    }
  }, []);

  // Handle patch comment error auto-dismiss
  useEffect(() => {
    if (patchCommentError) {
      setShowPatchErrorNotification(true);
      const timer = setTimeout(() => {
        setShowPatchErrorNotification(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [patchCommentError]);

 // Reset edit state when applicationId changes
  useEffect(() => {
    setEditingRowIndex(null);
    setEditingText("");
    setEditingCommentId(null);
    setErrorMessage("");
    setErrorHeader("");
    setFieldErrors({});
    setShowSuccessNotification(false);
    setShowCommentBox(false);
    setNewCommentDate("");
    setNewCommentType("--Select--");
    setNewCommentText("");
  }, [applicationId]);
  useEffect(() => {
    if (applicationId) {
      dispatch<any>(getTuitionAdminComments({ applicationId, nuid }));
    }
  }, [applicationId, nuid, dispatch]);

  // Fetch comment type options via tuition options (fieldName: comment)
  useEffect(() => {
    dispatch<any>(fetchTuitionOptions({ type: "comment", applType: "AT001" }));
  }, [dispatch]);

  // Normalize comments to an array in case API returns an object wrapper
  const normalizedComments: any[] = (() => {
    const commentsArray = [comments, comments?.data, comments?.comments];
    return safeGet(commentsArray.find((c) => Array.isArray(c)), []);
  })();

  // Map API comments to table rows; enable edit only if the last updated by matches the logged-in user
  const documentRows = normalizedComments.map((c: any, idx: number) => {
  const lastUpdater = c?.lastUpdatedBy ? String(c.lastUpdatedBy).trim() : "";
  const canEdit = !!lastUpdater && !!loggedInUserId && lastUpdater.toLowerCase() === loggedInUserId.toLowerCase();

    return {
      commentDt: safeGet(c.commentDt, "-"),
      commentDateType: safeGet(c.commentDateType, "-"),
      comments: safeGet(c.comments, "-"),
      lastUpdatedBy: c.lastUpdatedBy===null?safeGet(c.createdBy, "-"):safeGet(c.lastUpdatedBy, "-"),
      lastUpdatedTime: c.lastUpdatedTime===null?formatTimestampMDYHMS(c.createdTime):formatTimestampMDYHMS(c.lastUpdatedTime),
      action: (
        <EditButton
          disabled={!canEdit}
          onClick={() => {
            if (canEdit) {
              setEditingRowIndex(idx);
              setEditingText(safeGet(c.comments, ""));
              const cid = c.emplApplictnCommentId;
              setEditingCommentId(
                typeof cid === "string" || typeof cid === "number"
                  ? String(cid)
                  : null
              );
            }
          }}
        />
      ),
    };
  });

  const commentOptions = useMemo(() => {
    const base = ["--Select--"];
    if (Array.isArray(commentTypeOptions) && commentTypeOptions.length > 0) {
      return base.concat(commentTypeOptions);
    }
    return base;
  }, [commentTypeOptions]);

  const handleAddComment = () => {
    console.log('📊 Analytics: Add comment clicked')
    trackButtonClick('Add comment', 'tuition-admin-comments')
    setShowCommentBox(true);
  };

  const handleCloseCommentBox = () => {
    setShowCommentBox(false);
    setNewCommentDate("");
    setNewCommentType("--Select--");
    setNewCommentText("");
    setErrorMessage("");
    setErrorHeader("");
    setFieldErrors({});
  };

  // Inline edit handlers
  const handleEditCancel = () => {
    setEditingRowIndex(null);
    setEditingText("");
    setErrorMessage("");
    setErrorHeader("");
  };

  const handleEditSave = async () => {
    console.log('📊 Analytics: Save comment edit clicked')
    trackButtonClick('Save comment edit', 'tuition-admin-comments')
    if (editingRowIndex === null) return;
    const newText = editingText.trim();
    if (!newText) {
      setErrorHeader("Comment text required");
      setErrorMessage("Comment text cannot be empty.");
      return;
    }

    // Clear any previous errors
    setErrorMessage("");
    setErrorHeader("");

    // Attempt server patch if we have required identifiers
    if (applicationId && editingCommentId) {
      const action = await dispatch<any>(
        patchTuitionAdminEditComment({
          applicationId,
          commentId: editingCommentId,
          data: { commentText: newText },
          nuid,
        })
      );
      if (patchTuitionAdminEditComment.fulfilled.match(action)) {
        dispatch<any>(getTuitionAdminComments({ applicationId, nuid }));
        trackFormSave('tuition', undefined, { applicationId })

        // Show success notification
        setShowSuccessNotification(true);

        // Auto-hide notification after 5 seconds
        setTimeout(() => {
          setShowSuccessNotification(false);
        }, 5000);
      } else if (patchTuitionAdminEditComment.rejected.match(action)) {
        console.log("rejected");
      }
    }

    setEditingRowIndex(null);
    setEditingText("");
    setEditingCommentId(null);
  };

  const handleSaveComment = async () => {
    console.log('📊 Analytics: Save new comment clicked')
    trackButtonClick('Save new comment', 'tuition-admin-comments')
    if (!applicationId) {
      setErrorHeader("Missing Information");
      setErrorMessage("Missing application ID");
      return;
    }

    // Validate all three required fields
    const trimmedText = newCommentText.trim();
    const isCommentTypeValid = newCommentType && newCommentType !== "--Select--";
    const isCommentTextValid = !!trimmedText;

    // Validate date format (MM/DD/YYYY)
  const dateRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/\d{4}$/;
    const isDateValid = !!newCommentDate && dateRegex.test(newCommentDate);

    // Build field errors object
    const errors: Record<string, string> = {};
    const missingFields = [];
    if (!newCommentDate) {
      errors.date = "Date is required";
      missingFields.push("Date is required");
    } else if (!isDateValid) {
      errors.date = "Date must be a valid date in MM/DD/YYYY format";
      missingFields.push("Date must be a valid date in MM/DD/YYYY format");
    }
    if (!isCommentTypeValid) {
      errors.commentType = "Comment Type is required";
      missingFields.push("Comment Type is required");
    }
    if (!isCommentTextValid) {
      errors.commentText = "Comment Content is required";
      missingFields.push("Comment Content is required");
    }

    // Check if all fields are filled
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setErrorHeader("Required Information Missing");
      setErrorMessage(missingFields.join("\n"));
      return;
    }

    // Clear any previous errors
    setFieldErrors({});
    setErrorMessage("");
    setErrorHeader("");
    setIsSaving(true);
    // Build payload with all required fields
    const payload: Record<string, string> = {
      commentDate: newCommentDate,
      commentText: trimmedText,
      commentType: newCommentType,
    };

    const action = await dispatch<any>(
      saveTuitionAdminComment({
        applicationId,
        data: payload as any,
        nuid,
      })
    );
    if (saveTuitionAdminComment.fulfilled.match(action)) {
      dispatch<any>(getTuitionAdminComments({ applicationId, nuid }));
      handleCloseCommentBox();

      // Show success notification
      setShowSuccessNotification(true);

      // Auto-hide notification after 5 seconds
      setTimeout(() => {
        setShowSuccessNotification(false);
      }, 5000);
    } else {
      setErrorHeader("Save Failed");
      setErrorMessage("Failed to save comment");
    }
     setIsSaving(false);
  };

  return (
    <div className="margin-top-2u">
      <p className="p3 -book mt-5! ml-5!">
        {" "}
        <b>Date Content</b>
      </p>
      <p className="p4 -book ml-5! mt-2!">Select a date to view it's content</p>

      {/* Add Comment Button */}

      {/* Table or empty-state */}
      <div className="mt-4! ml-5!">
        {documentRows.length > 0 ? (
          <Table
            columns={documentColumns}
            rows={documentRows}
            showPagination={false}
            caption=" "
          />
        ) : (
          <p className="p3 -book text-center mt-2.5!">
            No comments found for this application.
          </p>
        )}
      </div>
           {/* Custom styles for wider dropdowns */}
           <style>{`
          /* Ensure the dropdown wrapper itself is a block that fills its container */
          .comment-dropdown-wide {
            display: block !important;
            width: 100% !important;
            box-sizing: border-box !important;
            max-width: 100% !important;
            position: relative !important; /* ensure absolute menu positions against this */
          }

          /* Ensure the internal drop-menu container uses relative positioning so the absolute menu aligns to it */
          .comment-dropdown-wide .ds-dropdown__drop-menu {
            position: relative !important;
            width: 100% !important;
            box-sizing: border-box !important;
          }

          /* Make native wrap and trigger button span full width */
          .comment-dropdown-wide .ds-dropdown__native-wrap,
          .comment-dropdown-wide .ds-dropdown__trigger-button,
          .comment-dropdown-wide .ds-dropdown__trigger-text,
          .comment-dropdown-wide .drop-menu-dropdown,
          .comment-dropdown-wide button.drop-menu-dropdown,
          .comment-dropdown-wide .drop-menu {
            width: 100% !important;
            min-width: 0 !important;
            max-width: 100% !important;
            box-sizing: border-box !important;
            display: block !important;
          }

          /* Force trigger button to be flex and full width */
          .comment-dropdown-wide .ds-dropdown__trigger-button {
            display: flex !important;
            align-items: center !important;
            width: 100% !important;
            box-sizing: border-box !important;
            padding: 0.5rem 0.75rem !important;
          }

          /* Ensure the visible menu aligns to trigger and fills its width */
          .comment-dropdown-wide .ds-dropdown__menu-list,
          .comment-dropdown-wide .drop-menu__list,
          .comment-dropdown-wide .drop-menu__items {
            min-width: 100% !important;
            width: 100% !important;
            max-width: 100% !important;
            left: 0 !important;
            right: auto !important;
            box-sizing: border-box !important;
            max-height: 400px !important;
            overflow-y: auto !important;
            overflow-x: hidden !important;
            position: absolute !important;
          }

          .comment-dropdown-wide .ds-dropdown__menu-list li,
          .comment-dropdown-wide .ds-dropdown__menu-list button,
          .comment-dropdown-wide .drop-menu__list li,
          .comment-dropdown-wide .drop-menu__list button {
            width: 100% !important;
            box-sizing: border-box !important;
          }

          /* Make native select full width (fallback) */
          .comment-dropdown-wide select {
            width: 100% !important;
            min-width: 0 !important;
            box-sizing: border-box !important;
          }

          /* Keep trigger text left aligned and chevron to right */
          .comment-dropdown-wide .ds-dropdown__trigger-text {
            text-align: left !important;
            justify-content: flex-start !important;
            overflow: hidden !important;
          }
          .comment-dropdown-wide .ds-dropdown__trigger-button {
            text-align: left !important;
            justify-content: space-between !important;
          }

        `}</style>

      {/* Success Notification */}
      {showSuccessNotification && (
        <div className="ml-5! mr-5! mt-4!">
          <Notifications
            messageInfo="Comment content has been updated successfully."
            status={true}
          />
        </div>
      )}

      {/* Patch Comment Error Notification */}
      {showPatchErrorNotification && patchCommentError && (
        <div className="ml-5! mr-5! mt-4!">
          <Notifications
            messageInfo={patchCommentError}
            status={false}
          />
        </div>
      )}

      {/* Error Banner */}
      {errorMessage && (
        <div className="ml-5! mr-5! mt-4!">
          <div
            className="ds-banner ds-banner__bg--white ds-banner--icon p-6! mb-6! mt-6! border-red-600!"
            data-ds-variant="basic"
            data-ds-version="1"
            data-ds-theme="vessel"
            role="alert"
            aria-live="assertive"
          >
            <div className="ds-banner__icon-img">
              <img
                src="/assets/images/errorCircleSmall.svg"
                alt=""
                style={{ width: '42px', height: '42px' }}
              />
            </div>
            <div className="ds-banner__container">
              <div className="ds-banner__main flex flex-col">
                <div
                  className="ds-banner__main-header--awareness"
                  style={{ fontWeight: 350, fontSize: '20px', marginBottom: '6px' }}
                >
                  {errorHeader}
                </div>
                <div className="ds-banner__main-body" style={{ fontSize: '16px' }}>
                  <ul style={{ listStyleType: 'disc', paddingLeft: '20px', margin: 0 }}>
                    {errorMessage.split('\n').map((line, index) => (
                      <li key={`error-${line}-${index}`}>{line}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Inline Edit Section (shows when Edit is clicked) */}
      {editingRowIndex !== null && (
        <div className="p-4 mt-6! ml-5! rounded-3xl border-2 border-(--Color-Graphite-Dark,#D3D6DC)">
          <div className="ml-4">
            <div className="w-full p-4! mt-8! mb-8! ml-5!">
              <label
                htmlFor="comment"
                className="block text-sm font-medium! text-black! mb-2!"
              >
                <b>Comment content</b>
              </label>
              <textarea
                autoComplete="on"
                id="commentText"
                name="commentText"
                className="text-area_word-count text-area_global -book text-base! text-black! w-full min-w-0 block "
                rows={6}
                value={editingText}
                onChange={(e) => setEditingText(e.target.value)}
              />
            </div>
            <div className="flex justify-end mt-6 pr-6! pb-6! gap-2">
              <button
                type="button"
                className="button -secondary"
                onClick={handleEditCancel}
              >
                Cancel
              </button>
              <button
                type="button"
                className="button -primary -light"
                onClick={handleEditSave}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
      {!showCommentBox && (
        <div className="mt-4! ml-5!">
          <button
            className="expandable-heading flex items-center gap-2 pt-2"
            data-analytics-type="button"
            data-analytics-click="TITLE"
            onClick={handleAddComment}
          >
            <span className="expandable-heading-icon text-xl text-[#0074AD] font-semibold">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M19 12C19 12.2652 18.8946 12.5196 18.7071 12.7071C18.5196 12.8946 18.2652 13 18 13H13V18C13 18.2652 12.8946 18.5196 12.7071 18.7071C12.5196 18.8946 12.2652 19 12 19C11.7348 19 11.4804 18.8946 11.2929 18.7071C11.1054 18.5196 11 18.2652 11 18V13H6C5.73478 13 5.48043 12.8946 5.29289 12.7071C5.10536 12.5196 5 12.2652 5 12C5 11.7348 5.10536 11.4804 5.29289 11.2929C5.48043 11.1054 5.73478 11 6 11H11V6C11 5.73478 11.1054 5.48043 11.2929 5.29289C11.4804 5.10536 11.7348 5 12 5C12.2652 5 12.5196 5.10536 12.7071 5.29289C12.8946 5.48043 13 5.73478 13 6V11H18C18.2652 11 18.5196 11.1054 18.7071 11.2929C18.8946 11.4804 19 11.7348 19 12Z"
                  fill="#0074AD"
                />
              </svg>
            </span>
            <span className="expandable-heading-text  text-[#0074AD] font-semibold">
              Add new comment
            </span>
          </button>
        </div>
      )}
      {/* Comment Box */}
      {showCommentBox && (
        <div className="p-4 mt-5! ml-5! rounded-3xl border-2 border-(--Color-Graphite-Dark,#D3D6DC)">
          <div className="ml-8! mt-8!">
            <p className="p3 -book">
              {" "}
              <b>Creating new comment</b>
            </p>

            {/* Custom Datepicker */}
            <div className="mb-4 w-[375px] pl-4 mt-8!">
              <CustomDatepicker
                name="date"
                label={"Select Date"}
                value={newCommentDate}
                onChange={(_name: string, value: string) =>
                  setNewCommentDate(value)
                }
                helperText={fieldErrors.date ? "" : "MM/DD/YYYY"}
                error={fieldErrors.date}
              />
            </div>

            <div className="mb-4 pl-4 mt-8!">
              <div className="w-[375px]">
                <div
                  className="ds-form w-full"
                  data-ds-theme="vessel"
                  data-ds-variant="select"
                  data-ds-version="1"
                >
                  <div className="w-full relative">
                    {/* <label htmlFor="commentType" className="p4 -medium">
                      Comment Type
                    </label> */}
                    <Dropdown
              label=" Comment Type"
              selectedValue={newCommentType}
              options={commentOptions}
              onSelect={(val: string) => setNewCommentType(val)}
              className="comment-dropdown-wide"
            />
                    {/* <Dropdown
                      fieldSelect={true}
                      fieldName="commentType"
                      selectedValue={newCommentType}
                      options={commentOptions}
                      onSelect={(val: string) => setNewCommentType(val)}
                      className="w-full"
                      placeholder="--Select--"
                      label=""
                      hasError={!!fieldErrors.commentType}
                    /> */}
                  </div>
                </div>
              </div>
              {fieldErrors.commentType && (
                <div className="error-description">
                  <ul id="commentType-error-description-list-1">
                    <li>
                      <img
                        src="/assets/images/errorCircleSmall.svg"
                        alt=""
                        width="14"
                        height="14"
                      />
                      <span className="screenreader-only">Error</span>
                      {fieldErrors.commentType}
                    </li>
                  </ul>
                </div>
              )}
            </div>

            {/* Text Area */}
            <div className="w-full p-4 mt-8! mb-8! ">
              <label
                htmlFor="comment"
                className="p4 -medium mb-2"
              >
                Comment content
              </label>
              <textarea
                autoComplete="on"
                id="commentText"
                name="commentText"
                className={`text-area_word-count text-area_global -book text-base! text-black! w-full min-w-0 block p-4 ${
                  fieldErrors.commentText ? 'field-error' : ''
                }`}
                rows={6}
                value={newCommentText}
                onChange={(e) => setNewCommentText(e.target.value)}
              />
              {fieldErrors.commentText && (
                <div className="error-description">
                  <ul id="commentText-error-description-list-1">
                    <li>
                      <img
                        src="/assets/images/errorCircleSmall.svg"
                        alt=""
                        width="14"
                        height="14"
                      />
                      <span className="screenreader-only">Error</span>
                      {fieldErrors.commentText}
                    </li>
                  </ul>
                </div>
              )}
            </div>

            {/* Close and Save Buttons */}
            <div className="flex justify-end mt-6 pr-6! pb-6! gap-2">
              <button
                type="button"
                className="button -secondary"
                onClick={handleCloseCommentBox}
              >
                Close
              </button>

              <button
                type="button"
                className="button -primary -light"
                data-analytics-type="button"
                data-analytics-click="SaveComment"
                onClick={handleSaveComment}
                disabled={isSaving}
              >
                 {isSaving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Comments;